<script lang="ts">
	import type { ConfiguratorContext } from '../contexts';

	type Props = {
		ctx: ConfiguratorContext;
	};

	let { ctx }: Props = $props();

	// Widget state
	let isOpen = $state(false);
	let activeTab = $state<'full' | 'state' | 'product'>('full');
	let copyFeedback = $state('');

	// Copy to clipboard helper
	async function copyToClipboard(data: any, label: string) {
		try {
			const json = JSON.stringify(data, null, 2);
			await navigator.clipboard.writeText(json);
			copyFeedback = `${label} copied!`;
			setTimeout(() => {
				copyFeedback = '';
			}, 2000);
		} catch (err) {
			copyFeedback = 'Copy failed';
			setTimeout(() => {
				copyFeedback = '';
			}, 2000);
		}
	}

	// Get full state for easy LLM sharing
	const fullState = $derived(() => {
		// Build attribute labels map
		const attributeLabels: Record<string, string> = {};
		ctx.productData.attributeNames.forEach((attr) => {
			attributeLabels[attr] = ctx.productData.getAttributeLabel(attr);
		});

		// Build terms map with images
		const termsMap: Record<string, any[]> = {};
		ctx.productData.attributeNames.forEach((attr) => {
			const terms = ctx.productData.getTermsForAttribute(attr);
			termsMap[attr] = terms.map((term) => ({
				id: term.id,
				name: term.name,
				slug: term.slug,
				image: term.image || null,
				image_id: term.image_id || null,
				order: term.order
			}));
		});

		return {
			product: {
				id: ctx.productData.configData?.product.id,
				name: ctx.productData.configData?.product.name,
				sku: ctx.productData.configData?.product.sku,
				configuration_mode: ctx.productData.configData?.product.configuration_mode,
				is_balloon_product: ctx.productData.isBalloonProduct,
				current_language: ctx.productData.currentLanguage
			},
			attributes: {
				names: ctx.productData.attributeNames,
				labels: attributeLabels,
				full: ctx.productData.attributes
			},
			terms: termsMap,
			selections: {
				attributes: ctx.selection.selections,
				quantity: ctx.selection.quantity,
				useCustomQuantity: ctx.selection.useCustomQuantity,
				customQuantity: ctx.selection.customQuantity,
				isComplete: ctx.selection.isComplete,
				selectedColors: ctx.selection.selectedColors
			},
			currentVariation: ctx.selection.currentVariation,
			pricing: {
				selectedTier: ctx.pricing.selectedTier,
				availableTiers: ctx.pricing.availableTiers,
				unitPrice: ctx.pricing.unitPrice,
				setupCostPerUnit: ctx.pricing.setupCostPerUnit,
				subtotal: ctx.pricing.subtotal,
				shipping: ctx.pricing.shipping,
				total: ctx.pricing.total,
				deliveryDate: ctx.pricing.deliveryDate
			},
			cart: {
				adding: ctx.cart.adding,
				message: ctx.cart.message,
				messageType: ctx.cart.messageType
			},
			apiData: {
				totalVariations: ctx.productData.variations.length,
				totalAttributes: ctx.productData.attributeNames.length
			}
		};
	});

	// Tab definitions
	const tabs = [
		{ id: 'full', label: 'Full State', icon: '🌐' },
		{ id: 'state', label: 'Current State', icon: '⚡' },
		{ id: 'product', label: 'Product Data', icon: '📦' }
	] as const;
</script>

<div class="wpc-debug-widget">
	<!-- Toggle Button -->
	<button class="debug-toggle" onclick={() => (isOpen = !isOpen)} title="Debug Widget">
		🐛
		{#if ctx.productData.error}
			<span class="debug-badge error">!</span>
		{/if}
	</button>

	<!-- Debug Panel -->
	{#if isOpen}
		<div class="debug-panel">
			<!-- Header -->
			<div class="debug-header">
				<div class="debug-title">
					<span>🐛</span>
					<strong>Debug Console</strong>
				</div>
				<button class="debug-close" onclick={() => (isOpen = false)} title="Close">×</button>
			</div>

			<!-- Copy Feedback Toast -->
			{#if copyFeedback}
				<div class="copy-toast">{copyFeedback}</div>
			{/if}

			<!-- Tabs -->
			<div class="debug-tabs">
				{#each tabs as tab}
					<button
						class="debug-tab"
						class:active={activeTab === tab.id}
						onclick={() => (activeTab = tab.id)}
					>
						<span class="tab-icon">{tab.icon}</span>
						<span class="tab-label">{tab.label}</span>
					</button>
				{/each}
			</div>

			<!-- Tab Content -->
			<div class="debug-content">
				{#if activeTab === 'full'}
					<div class="debug-section">
						<h3>Full State Export</h3>
						<p class="info">
							Complete configurator state in JSON format - perfect for sharing with LLMs or support.
						</p>

						<div class="json-preview">
							<pre><code>{JSON.stringify(fullState(), null, 2)}</code></pre>
						</div>

						<button
							class="copy-btn primary"
							onclick={() => copyToClipboard(fullState(), 'Full State')}
						>
							📋 Copy Full State JSON
						</button>

						<button
							class="copy-btn"
							onclick={() =>
								copyToClipboard(ctx.productData.configData, 'Complete API Response')}
						>
							📋 Copy Complete API Response
						</button>
					</div>
				{:else if activeTab === 'state'}
					<div class="debug-section">
						<h3>Current State</h3>

						<!-- Overview -->
						<h4>📊 Overview</h4>
						<dl>
							<dt>Product ID:</dt>
							<dd>{ctx.productData.configData?.product.id || '-'}</dd>
							<dt>Product Name:</dt>
							<dd>{ctx.productData.configData?.product.name || '-'}</dd>
							<dt>Configuration Complete:</dt>
							<dd>{ctx.selection.isComplete ? '✅ Yes' : '❌ No'}</dd>
							<dt>Matched Variation:</dt>
							<dd>
								{ctx.selection.currentVariation?.id || '- (none)'}
								{#if ctx.selection.currentVariation}
									<br />
									<small>SKU: {ctx.selection.currentVariation?.sku}</small>
								{/if}
							</dd>
							<dt>Loading:</dt>
							<dd>{ctx.productData.loading ? '⏳ Yes' : '✅ No'}</dd>
							<dt>Error:</dt>
							<dd>{ctx.productData.error || '✅ None'}</dd>
						</dl>

						<!-- Selections -->
						<h4>✅ Selections</h4>
						<dl>
							{#each Object.entries(ctx.selection.selections) as [key, value]}
								{@const label = ctx.productData.getAttributeLabel(key)}
								<dt>{label} ({key}):</dt>
								<dd>{value || '-'}</dd>
							{/each}
							<dt>Quantity:</dt>
							<dd>{ctx.selection.quantity || '-'}</dd>
							<dt>Use Custom Quantity:</dt>
							<dd>{ctx.selection.useCustomQuantity ? 'Yes' : 'No'}</dd>
							{#if ctx.selection.useCustomQuantity}
								<dt>Custom Quantity:</dt>
								<dd>{ctx.selection.customQuantity}</dd>
							{/if}
							{#if ctx.selection.customQuantityError}
								<dt class="error">Error:</dt>
								<dd class="error">{ctx.selection.customQuantityError}</dd>
							{/if}
						</dl>

						<!-- Pricing -->
						<h4>💰 Pricing</h4>
						<dl>
							<dt>Unit Price:</dt>
							<dd>€{ctx.pricing.unitPrice.toFixed(4)}</dd>
							<dt>Setup Cost/Unit:</dt>
							<dd>€{ctx.pricing.setupCostPerUnit.toFixed(4)}</dd>
							<dt>Subtotal:</dt>
							<dd>€{ctx.pricing.subtotal.toFixed(2)}</dd>
							<dt>Shipping:</dt>
							<dd>€{ctx.pricing.shipping.toFixed(2)}</dd>
							<dt>Total:</dt>
							<dd><strong>€{ctx.pricing.total.toFixed(2)}</strong></dd>
							<dt>Delivery Date:</dt>
							<dd>{ctx.pricing.deliveryDate || '-'}</dd>
						</dl>

						{#if ctx.pricing.availableTiers.length > 0}
							<h5>Available Tiers ({ctx.pricing.availableTiers.length})</h5>
							<div class="tiers-list">
								{#each ctx.pricing.availableTiers.slice(0, 5) as tier}
									<div class="tier-item" class:selected={ctx.pricing.selectedTier?.aantal === tier.aantal}>
										{tier.aantal} @ €{tier.prijs.toFixed(4)}
										{#if ctx.pricing.selectedTier?.aantal === tier.aantal}
											<span class="tier-badge">Selected</span>
										{/if}
									</div>
								{/each}
								{#if ctx.pricing.availableTiers.length > 5}
									<p class="info">...and {ctx.pricing.availableTiers.length - 5} more tiers</p>
								{/if}
							</div>
						{/if}

						<!-- Cart -->
						<h4>🛒 Cart</h4>
						<dl>
							<dt>Adding to Cart:</dt>
							<dd>{ctx.cart.adding ? '⏳ Yes' : '✅ No'}</dd>
							{#if ctx.cart.message}
								<dt>Message:</dt>
								<dd>{ctx.cart.message}</dd>
								<dt>Type:</dt>
								<dd>{ctx.cart.messageType}</dd>
							{/if}
						</dl>

						<button
							class="copy-btn"
							onclick={() => copyToClipboard({
								selections: ctx.selection.selections,
								quantity: ctx.selection.quantity,
								pricing: {
									unitPrice: ctx.pricing.unitPrice,
									total: ctx.pricing.total,
									selectedTier: ctx.pricing.selectedTier
								},
								cart: {
									adding: ctx.cart.adding,
									message: ctx.cart.message
								}
							}, 'Current State')}
						>
							📋 Copy Current State JSON
						</button>
					</div>
				{:else if activeTab === 'product'}
					<div class="debug-section">
						<h3>Product Data</h3>
						{#if ctx.productData.configData}
							<!-- Base Product Info -->
							<h4>📦 Product Info</h4>
							<dl>
								<dt>ID:</dt>
								<dd>{ctx.productData.configData.product.id}</dd>
								<dt>Name:</dt>
								<dd>{ctx.productData.configData.product.name}</dd>
								<dt>SKU:</dt>
								<dd>{ctx.productData.configData.product.sku}</dd>
								<dt>Setup Costs:</dt>
								<dd>€{ctx.productData.configData.product.instelkosten?.toFixed(2) || '0.00'}</dd>
								<dt>Delivery Time:</dt>
								<dd>{ctx.productData.configData.product.levertijd || '-'}</dd>
								<dt>Total Variations:</dt>
								<dd>{ctx.productData.variations.length}</dd>
							</dl>

							<h4>Attributes ({ctx.productData.attributeNames.length})</h4>
							<div class="attributes-detail">
								{#each ctx.productData.attributeNames as attr}
									{@const label = ctx.productData.getAttributeLabel(attr)}
									{@const terms = ctx.productData.getTermsForAttribute(attr)}
									<div class="attribute-block">
										<h5>{label}</h5>
										<p class="attribute-slug">{attr}</p>
										<div class="terms-grid">
											{#each terms as term}
												<div class="term-item">
													{#if term.image}
														<img src={term.image} alt={term.name} class="term-image" />
													{/if}
													<div class="term-info">
														<strong>{term.name}</strong>
														<small>{term.slug}</small>
													</div>
												</div>
											{/each}
										</div>
									</div>
								{/each}
							</div>

							<!-- Current Variation -->
							<h4>🔧 Current Variation</h4>
							{#if ctx.selection.currentVariation}
								<dl>
									<dt>ID:</dt>
									<dd>{ctx.selection.currentVariation?.id}</dd>
									<dt>SKU:</dt>
									<dd>{ctx.selection.currentVariation?.sku}</dd>
									<dt>Stock Status:</dt>
									<dd>{ctx.selection.currentVariation?.stock_status}</dd>
									<dt>In Stock:</dt>
									<dd>{ctx.selection.currentVariation?.is_in_stock ? '✅' : '❌'}</dd>
									<dt>Shipping Group:</dt>
									<dd>{ctx.selection.currentVariation?.verzendgroep || '-'}</dd>
									<dt>Staffelprijzen Tiers:</dt>
									<dd>{ctx.selection.currentVariation?.staffelprijzen.length || 0}</dd>
								</dl>

								<h5>Variation Attributes</h5>
								<dl>
									{#each Object.entries(ctx.selection.currentVariation?.attributes || {}) as [key, value]}
										<dt>{key}:</dt>
										<dd>{value}</dd>
									{/each}
								</dl>
							{:else}
								<p class="empty">No variation matched with current selections</p>
							{/if}

							<button
								class="copy-btn"
								onclick={() =>
									copyToClipboard(
										{
											product: ctx.productData.configData.product,
											attributes: ctx.productData.attributeNames,
											variations: ctx.productData.variations,
											currentVariation: ctx.selection.currentVariation
										},
										'Product Data'
									)}
							>
								📋 Copy Product Data JSON
							</button>
						{:else}
							<p class="empty">No product data loaded</p>
						{/if}
					</div>
				{/if}
			</div>
		</div>
	{/if}
</div>

<style>
	.wpc-debug-widget {
		position: fixed;
		bottom: 20px;
		left: 20px;
		z-index: 99999;
		font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen-Sans, Ubuntu,
			Cantarell, 'Helvetica Neue', sans-serif;
	}

	.debug-toggle {
		width: 56px;
		height: 56px;
		border-radius: 50%;
		background: var(--wp--preset--color--contrast, #000);
		color: var(--wp--preset--color--base, #fff);
		border: none;
		font-size: 28px;
		cursor: pointer;
		box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
		transition: all 0.2s;
		position: relative;
		display: flex;
		align-items: center;
		justify-content: center;
	}

	.debug-toggle:hover {
		transform: scale(1.1);
		box-shadow: 0 6px 16px rgba(0, 0, 0, 0.4);
	}

	.debug-badge {
		position: absolute;
		top: -2px;
		right: -2px;
		background: #f44336;
		color: white;
		width: 20px;
		height: 20px;
		border-radius: 50%;
		display: flex;
		align-items: center;
		justify-content: center;
		font-size: 12px;
		font-weight: bold;
		border: 2px solid var(--wp--preset--color--base, #fff);
	}

	.debug-panel {
		position: absolute;
		bottom: 70px;
		left: 0;
		width: 500px;
		height: calc(100vh - 120px);
		max-height: 90vh;
		background: var(--wp--preset--color--base, #fff);
		color: var(--wp--preset--color--contrast, #000);
		border-radius: 8px;
		box-shadow: 0 8px 24px rgba(0, 0, 0, 0.25);
		display: flex;
		flex-direction: column;
		overflow: hidden;
	}

	.debug-header {
		background: var(--wp--preset--color--contrast, #000);
		color: var(--wp--preset--color--base, #fff);
		padding: 16px;
		display: flex;
		align-items: center;
		justify-content: space-between;
	}

	.debug-title {
		display: flex;
		align-items: center;
		gap: 8px;
		font-size: 16px;
	}

	.debug-title span {
		font-size: 20px;
	}

	.debug-close {
		background: transparent;
		border: none;
		color: inherit;
		font-size: 32px;
		cursor: pointer;
		padding: 0;
		line-height: 1;
		width: 32px;
		height: 32px;
		display: flex;
		align-items: center;
		justify-content: center;
		border-radius: 4px;
		transition: background 0.2s;
	}

	.debug-close:hover {
		background: rgba(255, 255, 255, 0.1);
	}

	.copy-toast {
		position: absolute;
		top: 60px;
		left: 50%;
		transform: translateX(-50%);
		background: #4caf50;
		color: white;
		padding: 8px 16px;
		border-radius: 4px;
		font-size: 14px;
		z-index: 10;
		animation: slideDown 0.3s ease;
		box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
	}

	@keyframes slideDown {
		from {
			opacity: 0;
			transform: translateX(-50%) translateY(-10px);
		}
		to {
			opacity: 1;
			transform: translateX(-50%) translateY(0);
		}
	}

	.debug-tabs {
		display: flex;
		overflow-x: auto;
		background: rgba(0, 0, 0, 0.05);
		border-bottom: 1px solid rgba(0, 0, 0, 0.1);
	}

	.debug-tab {
		flex-shrink: 0;
		padding: 12px 16px;
		background: transparent;
		border: none;
		cursor: pointer;
		display: flex;
		align-items: center;
		gap: 6px;
		font-size: 13px;
		color: inherit;
		border-bottom: 2px solid transparent;
		transition: all 0.2s;
	}

	.debug-tab:hover {
		background: rgba(0, 0, 0, 0.05);
	}

	.debug-tab.active {
		background: var(--wp--preset--color--base, #fff);
		border-bottom-color: var(--wp--preset--color--contrast, #000);
		font-weight: 600;
	}

	.tab-icon {
		font-size: 16px;
	}

	.debug-content {
		flex: 1;
		overflow-y: auto;
		padding: 16px;
	}

	.debug-section h3 {
		margin: 0 0 16px 0;
		font-size: 18px;
		color: var(--wp--preset--color--contrast, #000);
	}

	.debug-section h4 {
		margin: 16px 0 8px 0;
		font-size: 14px;
		font-weight: 600;
		color: var(--wp--preset--color--contrast, #000);
	}

	.debug-section h5 {
		margin: 12px 0 6px 0;
		font-size: 12px;
		font-weight: 600;
		color: rgba(0, 0, 0, 0.7);
		text-transform: uppercase;
		letter-spacing: 0.5px;
	}

	dl {
		margin: 0;
		display: grid;
		grid-template-columns: 140px 1fr;
		gap: 8px;
		font-size: 13px;
	}

	dt {
		font-weight: 600;
		color: rgba(0, 0, 0, 0.7);
	}

	dt.error {
		color: #f44336;
	}

	dd {
		margin: 0;
		word-break: break-word;
	}

	dd.error {
		color: #f44336;
	}

	.copy-btn {
		margin-top: 16px;
		padding: 10px 16px;
		background: var(--wp--preset--color--contrast, #000);
		color: var(--wp--preset--color--base, #fff);
		border: none;
		border-radius: 4px;
		cursor: pointer;
		font-size: 14px;
		font-weight: 500;
		transition: opacity 0.2s;
		width: 100%;
	}

	.copy-btn:hover {
		opacity: 0.9;
	}

	.copy-btn.primary {
		background: #4caf50;
		margin-bottom: 8px;
	}

	.empty {
		font-style: italic;
		color: rgba(0, 0, 0, 0.5);
		font-size: 13px;
		margin: 8px 0;
	}

	.info {
		background: rgba(33, 150, 243, 0.1);
		border-left: 3px solid #2196f3;
		padding: 8px 12px;
		font-size: 13px;
		margin: 8px 0;
		border-radius: 4px;
	}

	.tiers-list {
		display: flex;
		flex-direction: column;
		gap: 4px;
		margin: 8px 0;
	}

	.tier-item {
		padding: 6px 12px;
		background: rgba(0, 0, 0, 0.05);
		border-radius: 4px;
		font-size: 13px;
		display: flex;
		align-items: center;
		justify-content: space-between;
		position: relative;
	}

	.tier-item.selected {
		background: rgba(76, 175, 80, 0.15);
		border: 1px solid rgba(76, 175, 80, 0.5);
		font-weight: 600;
	}

	.tier-badge {
		background: #4caf50;
		color: white;
		padding: 2px 8px;
		border-radius: 12px;
		font-size: 11px;
		font-weight: 600;
		text-transform: uppercase;
	}

	.attributes-list {
		margin: 8px 0;
		padding-left: 20px;
		font-size: 13px;
	}

	.attributes-list li {
		margin: 4px 0;
	}

	.attributes-detail {
		margin: 12px 0;
	}

	.attribute-block {
		margin-bottom: 16px;
		padding: 12px;
		background: rgba(0, 0, 0, 0.02);
		border-radius: 6px;
		border: 1px solid rgba(0, 0, 0, 0.1);
	}

	.attribute-block h5 {
		margin: 0 0 4px 0;
		font-size: 14px;
		font-weight: 600;
		color: var(--wp--preset--color--contrast, #000);
	}

	.attribute-slug {
		margin: 0 0 8px 0;
		font-size: 11px;
		color: rgba(0, 0, 0, 0.5);
		font-family: 'Monaco', 'Menlo', monospace;
	}

	.terms-grid {
		display: grid;
		grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
		gap: 8px;
		margin-top: 8px;
	}

	.term-item {
		padding: 8px;
		background: var(--wp--preset--color--base, #fff);
		border: 1px solid rgba(0, 0, 0, 0.1);
		border-radius: 4px;
		font-size: 12px;
		display: flex;
		flex-direction: column;
		gap: 6px;
	}

	.term-image {
		width: 100%;
		height: 60px;
		object-fit: contain;
		border-radius: 2px;
		background: rgba(0, 0, 0, 0.02);
	}

	.term-info {
		display: flex;
		flex-direction: column;
		gap: 2px;
	}

	.term-info strong {
		font-size: 12px;
		font-weight: 600;
		color: var(--wp--preset--color--contrast, #000);
	}

	.term-info small {
		font-size: 10px;
		color: rgba(0, 0, 0, 0.5);
		font-family: 'Monaco', 'Menlo', monospace;
	}

	.json-preview {
		max-height: 300px;
		overflow: auto;
		background: rgba(0, 0, 0, 0.05);
		border-radius: 4px;
		padding: 12px;
		margin: 12px 0;
	}

	.json-preview pre {
		margin: 0;
		font-size: 11px;
		font-family: 'Monaco', 'Menlo', monospace;
		line-height: 1.5;
	}

	.json-preview code {
		color: var(--wp--preset--color--contrast, #000);
	}

	@media (max-width: 500px) {
		.debug-panel {
			width: calc(100vw - 40px);
		}

		dl {
			grid-template-columns: 100px 1fr;
			font-size: 12px;
		}

		.tab-label {
			display: none;
		}
	}
</style>
